import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { OrganizationSwitcher } from '../OrganizationSwitcher';
import {
    Home,
    Package,
    Users,
    BarChart2,
    Tag,
    Settings,
    ArrowUpRight,
    TrendingUp,
    ShoppingBag,
    Image as ImageIcon,
    LayoutTemplate,
    Target,
    Gauge,
    FileBarChart,
    Palette,
    Globe,
    ChevronDown,
    ChevronRight,
    Search
} from 'lucide-react';
import { useState } from 'react';
import { cn } from "@/lib/utils";

export function AdminSidebar() {
    const { language } = useLanguage();
    const isRtl = language === 'ar';
    const location = useLocation();

    // Group State
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
        analytics: true,
        store: true,
        marketing: true
    });

    const toggleGroup = (key: string) => {
        setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const t = {
        home: language === 'en' ? 'Home' : 'الرئيسية',
        orders: language === 'en' ? 'Orders' : 'الطلبات',
        products: language === 'en' ? 'Products' : 'المنتجات',
        customers: language === 'en' ? 'Customers' : 'العملاء',
        content: language === 'en' ? 'Content' : 'المحتوى',
        analytics: language === 'en' ? 'Analytics' : 'التحليلات',
        marketing: language === 'en' ? 'Marketing' : 'التسويق',
        salesChannels: language === 'en' ? 'Sales Channels' : 'قنوات البيع',
        onlineStore: language === 'en' ? 'Online Store' : 'المتجر الإلكتروني',
        settings: language === 'en' ? 'Settings' : 'الإعدادات',

        // Sub Items
        dashboard: language === 'en' ? 'Dashboard' : 'لوحة التحكم',
        reports: language === 'en' ? 'Reports' : 'التقارير',
        liveView: language === 'en' ? 'Live View' : 'بث مباشر',
        funnels: language === 'en' ? 'Funnels' : 'مسارات البيع (Funnels)',
        discounts: language === 'en' ? 'Discounts' : 'الخصومات',
        campaigns: language === 'en' ? 'Campaigns' : 'الحملات',
        themes: language === 'en' ? 'Themes' : 'القوالب',
        pages: language === 'en' ? 'Pages' : 'الصفحات',
        navigation: language === 'en' ? 'Navigation' : 'القوائم',
    };

    const SidebarItem = ({ icon: Icon, label, to, isSubItem = false }: { icon: any, label: string, to: string, isSubItem?: boolean }) => {
        const isActive = location.pathname === to || location.pathname.startsWith(to + '/');

        return (
            <NavLink
                to={to}
                className={cn(
                    "flex items-center px-3 py-2 rounded-md cursor-pointer mb-0.5 transition-colors group",
                    isActive
                        ? 'bg-[#edeeef] text-[#008060] font-semibold'
                        : 'text-[#4a4a4a] hover:bg-[#f6f6f7]',
                    isSubItem && (isRtl ? "mr-6" : "ml-6")
                )}
            >
                {!isSubItem && <Icon size={20} className={cn("text-gray-500 group-hover:text-gray-700 transition-colors", isActive && "text-[#008060]", isRtl ? 'ml-3' : 'mr-3')} />}
                {isSubItem && <div className={cn("w-1.5 h-1.5 rounded-full mr-3 ml-1 bg-gray-400 group-hover:bg-gray-600 transition-colors", isActive && "bg-[#008060]")} />}
                <span className="text-sm">{label}</span>
            </NavLink>
        );
    };

    const SidebarGroup = ({ label, icon: Icon, id, children }: { label: string, icon: any, id: string, children: React.ReactNode }) => {
        const isOpen = openGroups[id];
        return (
            <div className="mb-1">
                <div
                    onClick={() => toggleGroup(id)}
                    className="flex items-center justify-between px-3 py-2 rounded-md cursor-pointer text-[#4a4a4a] hover:bg-[#f6f6f7] group"
                >
                    <div className="flex items-center">
                        <Icon size={20} className={cn("text-gray-500 group-hover:text-gray-700", isRtl ? 'ml-3' : 'mr-3')} />
                        <span className="text-sm font-medium">{label}</span>
                    </div>
                    {isOpen ? <ChevronDown size={14} className="text-gray-400" /> : (isRtl ? <ChevronRight size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />)}
                </div>
                {isOpen && (
                    <div className="mt-0.5">
                        {children}
                    </div>
                )}
            </div>
        );
    }

    return (
        <aside className="w-60 bg-[#f1f2f3] border-r border-[#e1e3e5] min-h-[calc(100vh-3.5rem)] hidden md:block py-4 px-2 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto scrollbar-hide">
            {/* Organization Switcher */}
            <div className="px-2 mb-4 pb-3 border-b border-gray-200">
                <OrganizationSwitcher />
            </div>

            <nav className="space-y-1">
                <SidebarItem icon={Home} label={t.home} to="/admin/dashboard" />
                <SidebarItem icon={ShoppingBag} label={t.orders} to="/admin/orders" />
                <SidebarItem icon={Tag} label={t.products} to="/admin/products" />
                <SidebarItem icon={Users} label={t.customers} to="/admin/customers" />
                <SidebarItem icon={ImageIcon} label={t.content} to="/admin/media" />

                <div className="my-2" />

                <SidebarGroup label={t.analytics} icon={BarChart2} id="analytics">
                    <SidebarItem icon={BarChart2} label={t.dashboard} to="/admin/analytics" isSubItem />
                    <SidebarItem icon={FileBarChart} label={t.reports} to="/admin/analytics/reports" isSubItem />
                    <SidebarItem icon={Gauge} label={t.liveView} to="/admin/analytics-real" isSubItem />
                </SidebarGroup>

                <SidebarGroup label={t.marketing} icon={Target} id="marketing">
                    <SidebarItem icon={ArrowUpRight} label={t.campaigns} to="/admin/marketing" isSubItem />
                    <SidebarItem icon={TrendingUp} label={t.funnels} to="/admin/funnels" isSubItem />
                    <SidebarItem icon={Tag} label={t.discounts} to="/admin/discount-codes" isSubItem />
                </SidebarGroup>

                <div className="my-2 border-t border-gray-200 mx-2" />

                <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-tighter">
                    {t.salesChannels}
                </div>

                <SidebarGroup label={t.onlineStore} icon={Globe} id="store">
                    <SidebarItem icon={LayoutTemplate} label={t.themes} to="/admin/themes" isSubItem />
                    <SidebarItem icon={LayoutTemplate} label={t.pages} to="/admin/pages" isSubItem />
                    <SidebarItem icon={Palette} label="Pro Templates" to="/admin/templates" isSubItem />
                </SidebarGroup>

                <div className="mt-8" />
                <SidebarItem icon={Settings} label={t.settings} to="/admin/settings" />
            </nav>
        </aside>
    );
}
